import React from 'react'
import { AnimatePresence } from 'framer-motion'
import styled from 'styled-components'

import { TowingOrderType, TypeIdentifier } from '../../common/types'
import { isActiveJob } from '../../containers/TowingJobsPage/countJobs'
import autoliitto from '../../images/autoliitto.png'
import lahitapiola from '../../images/lahitapiola.png'
import sos from '../../images/sos.png'
import groupTransfer from '../../images/group_transfer.svg'
import { Icon } from '../icons/Icon'
import { JobStatus } from '../JobStatus'
import { JobTypeDescription, getDeliverable } from '../JobTypeDescription'
import {
  ExpandedList,
  Heading3,
  ImageWrapper,
  List,
  ListColumn,
  ListRowWrapper,
  ListStatus,
  SimpleRow,
  Text,
} from '../layout'
import { Styling } from '../Styling'
import { NavigateTo } from '../NavigateTo'
import { TowingOrderScheduling } from '../TowingOrderScheduling'
import { VehicleMarkAndModel } from '../VehicleMarkAndModel'
import { TowingOrderJobInterface } from './types'
import { parseAddress } from '../Parsers'
import { getPrepaymentLabel } from '../TowingCommon/getPrepaymentLabel'
import { TowingPriorityCircle } from '../TowingCommon/TowingPriorityCircle'
import { getRepairShop } from './getRepairShop'
import { RepairShop } from '../../containers/TowingRecordPage/components/routes/subforms/FinishRoute/options'
import { useSocketLock } from '../../util/useSocketLock'

const StyledIcon = styled(Icon as any)`
  svg {
    width: 1rem;
    height: 1rem;
    margin-left: -2px;
  }
`
interface OrderRowProps {
  active: boolean
}

const OrderRow = styled(ListRowWrapper as any)<OrderRowProps>`
  ${props =>
    props.active && `background-color: ${props.color ? props.theme.colors[props.color] : props.theme.colors.orange50}`}
`

interface Props {
  repairShops: RepairShop[]
  towingOrder: TowingOrderJobInterface
  slug: string
  translateMessage: (msgId: string) => string
  expandedRow: boolean
  handleItemClick: (id: number) => void
  station: {
    name: string
  } | null
  operatorName: string
}

export const TowingOrderRow = React.memo<Props>(function TowingOrderRow({
  expandedRow,
  handleItemClick,
  towingOrder,
  slug,
  station,
  operatorName,
  repairShops,
}) {
  const locked = useSocketLock({
    entityType: 'towing_order',
    entityId: towingOrder.id,
    initialLockedFor: towingOrder.locked,
  })

  const repairShop = getRepairShop(repairShops, towingOrder)

  const prepaymentLabel =
    towingOrder.hasPrepayment &&
    getPrepaymentLabel(towingOrder?.prepaymentStatus, {
      fallback: 'maksulinkki lähetetty',
    })

  return (
    <OrderRow
      active={isActiveJob(towingOrder.status, towingOrder.operatorId)}
      onClick={() => handleItemClick(towingOrder.id)}
    >
      <List>
        <TowingPriorityCircle tag={towingOrder.priorityTag} />
        <ListColumn justify="center">
          <Heading3 wordBreak='break-all' maxWidth='100%' >
            {towingOrder.vehicleDetails.registrationNumber || '-'}
          </Heading3>
          <VehicleMarkAndModel markAndModel={towingOrder.vehicleDetails.makeAndModel} />
          <TowingOrderScheduling jobDetails={towingOrder.jobDetails} />
        </ListColumn>
        <ListColumn justify="center">
          {towingOrder.typeIdentifier === TypeIdentifier.autoliitto && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img src={autoliitto} alt="Autoliitto" width="55rem" />
            </ImageWrapper>
          )}
          {towingOrder.typeIdentifier === TypeIdentifier.sos && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img src={sos} alt="SOS" width={`40rem`} />
            </ImageWrapper>
          )}
          {towingOrder.typeIdentifier === TypeIdentifier.lahitapiola && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img
                src={`${towingOrder.type === TowingOrderType.sos ? sos : lahitapiola}`}
                alt="Lähitapiola"
                width={`40rem`}
              />
            </ImageWrapper>
          )}
          {towingOrder.typeIdentifier === TypeIdentifier.group_transfer && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img src={groupTransfer} alt="Other" width={`55rem`} />
            </ImageWrapper>
          )}
        </ListColumn>
        <ListColumn>
          <ListStatus status={towingOrder.status}>
            <JobStatus status={towingOrder.status} visibility={towingOrder.visibility} operatorName={operatorName} />
          </ListStatus>
          <Text color="grey400">
            <JobTypeDescription
              serviceType={towingOrder.jobDetails.serviceType}
              type={towingOrder.typeIdentifier}
              label={towingOrder.label}
              station={station}
              deliverable={getDeliverable(towingOrder.sosServiceOrder)}
            />
          </Text>
          {prepaymentLabel && (
            <Styling bold>
              <Text size="small">{prepaymentLabel}</Text>
            </Styling>
          )}
        </ListColumn>
        <ListColumn>
          <NavigateTo direction={locked ? 'locked' : 'forward'} slug={slug} />
        </ListColumn>
      </List>
      <AnimatePresence>
        {expandedRow && (
          <ExpandedList key={towingOrder.id}>
            <ListColumn>
              <SimpleRow justify={'flex-start'} marginMobileBottom="0">
                <StyledIcon icon="map-marker" color="black" size="x-small" marginRight="0.3rem" />
                <Text>{`${towingOrder.from.address} ${towingOrder.from.city} ${towingOrder.from.zipcode || ''}`}</Text>
              </SimpleRow>
              {towingOrder.from.description !== '' && (
                <SimpleRow marginMobileBottom="0" margin="0.25rem 0 0.25rem 1.25rem">
                  <Text>{`(${towingOrder.from.description})`}</Text>
                </SimpleRow>
              )}

              {repairShop &&
              towingOrder.typeIdentifier === TypeIdentifier.autoliitto &&
              towingOrder.jobDetails.serviceType !== 'towing' ? (
                <>
                  <SimpleRow justify={'flex-start'} marginMobileBottom="0" margin="0.25rem 0 0 0">
                    <StyledIcon icon="map-marker" color="black" size="x-small" marginRight="0.3rem" />
                    <Text fontWeight="bold">{`Jos työ muuttuu hinaukseksi:`}</Text>
                  </SimpleRow>
                  <SimpleRow marginMobileBottom="0" margin="0.25rem 0 0 1.25rem">
                    <Text>{parseAddress(repairShop.location.address, repairShop.location.city)}</Text>
                  </SimpleRow>
                  <SimpleRow marginMobileBottom="0" margin="0 0 0.25rem 1.25rem">
                    <Text>{`(${repairShop.name})`}</Text>
                  </SimpleRow>
                </>
              ) : (
                <>
                  <SimpleRow justify={'flex-start'} marginMobileBottom="0" margin="0.25rem 0 0 0">
                    <StyledIcon icon="map-marker" color="black" size="x-small" marginRight="0.3rem" />
                    <Text>{`${towingOrder.to.address} ${towingOrder.to.city} ${towingOrder.to.zipcode || ''}`}</Text>
                  </SimpleRow>
                  {towingOrder.to.description !== '' && (
                    <SimpleRow marginMobileBottom="0" margin="0.25rem 0 0.25rem 1.25rem">
                      <Text>{`(${towingOrder.to.description})`}</Text>
                    </SimpleRow>
                  )}
                </>
              )}
            </ListColumn>
            <ListColumn />
            <ListColumn>
              <Text>{towingOrder.jobDetails.notes}</Text>
            </ListColumn>
            <ListColumn />
          </ExpandedList>
        )}
      </AnimatePresence>
    </OrderRow>
  )
})
